/**
 * What: Component-level Shopify app copy used by the embedded app surfaces.
 * Transport / data-flow: Pure TypeScript constants with no runtime behavior.
 */
export const shopifyComponents = {
    appShell: {
        createNewStudyAction: 'Create new test',
        createAudienceSetAction: 'Create audience',
        pageGeneratedPrefix: 'Page generated:',
        pageGeneratedUnavailable: 'Page generated time unavailable.',
        microSurveysUnavailableTitle: 'Micro-surveys unavailable',
        microSurveysUnavailableMessage: 'Micro-surveys are coming soon.',
        pixelEventsUnavailableTitle: 'Pixel events unavailable',
        pixelEventsUnavailableMessage: 'Pixel events are coming soon.',
        connectedBannerTitle: 'TestFeed account connected',
        connectedBannerBody: 'This Shopify store is now linked to your active TestFeed organization.',
    },
    capabilityComingSoonCard: {
        comingSoonLabel: 'Coming soon',
    },
    methodologyPreflight: {
        modal: {
            cannotRunStudyTitle: 'Cannot run test',
            readyToRunTitle: 'Ready to run',
            readyToRunDescription: "You can close this page and come back at any time to check progress and see results when they're ready.",
            shopperBackgroundProofTitle: 'Shopper background proof',
            shopperBackgroundProofDescription: 'These aggregate shopper-background fields were checked during dry run before the test starts.',
            sourceBucketSingular: 'source bucket',
            sourceBucketPlural: 'source buckets',
            sourceRecordSingular: 'source record',
            sourceRecordPlural: 'source records',
            shopperBackgroundFallbackLabel: 'Shopper background',
            providerLabels: {
                posthog: 'PostHog',
                amplitude: 'Amplitude',
                shopify: 'Shopify',
                ga4: 'Google Analytics 4',
                heap: 'Heap',
                mixpanel: 'Mixpanel',
                fallback: 'Analytics',
            },
            runStudyButton: 'Run Test',
            cancelButton: 'Cancel',
        },
        panel: {
            readyToRunLabel: 'Ready to Run',
            needsAttentionLabel: 'Needs attention',
            needsAttentionText: 'This test needs attention before it can be prepared.',
            readyToRunText: 'When you save this setup, TestFeed will create a test draft and take you straight to the final run review.',
            audienceStoreShapedTitle: 'Store-shaped shopper audience',
            audienceGeneralTitle: 'General shopper audience',
            audienceStoreShapedCopy: 'This audience uses broad patterns from your store, such as purchase history, spend behavior, and product interest. These are simulated shoppers, not a survey of your actual customers.',
            audienceGeneralCopy: 'This audience uses a broad group of simulated shoppers for a directional product read. These are simulated shoppers, not a survey of your actual customers.',
            howToReadTitle: 'How to read the result',
            howToReadCopy: 'This is a directional product read to help decide what to improve, promote, or test next. Results are directional. They compare versions inside this test only.',
            reviewRequiredTitle: 'Review required',
            whatHappensNextTitle: 'What happens next',
        },
    },
    studyRunPage: {
        pageTitle: 'Review and run test',
        generalAudienceLabel: 'General shoppers',
        readyStatus: 'Ready to run',
        runningStatus: 'Test running',
        reviewHeading: 'Review your setup',
        productLabel: 'Product',
        audienceLabel: 'Audience',
        savedSetupBody: 'Your setup is saved. Check its readiness below before starting the test.',
        dryRunBody: 'We check the setup again before the test starts.',
        runStudyAction: 'Run test',
        backToStudiesAction: 'Back to tests',
        checkingReadiness: 'Checking test readiness…',
        retryReadinessAction: 'Try again',
        studyStartedBody: "Your test has started. You can leave this page; we'll notify you when results are ready.",
        viewTestProgressAction: 'View test progress',
        missingSetupBody: "We couldn't find this saved setup. Return to Tests and choose a setup to review.",
        missingPacketBody: 'This setup is not ready to run yet. Return to the test builder to review it.',
        runErrorBody: "We couldn't start this test. Please try again.",
    },
    preparedSetupsPanel: {
        refreshingSetupsText: 'Refreshing prepared setups...',
        noSetupsHeading: 'Prepared test setups',
        noSetupsDescription: 'No prepared setups yet',
        noSetupsAction: 'Create new test',
        noSetupsBody: 'Prepare a product test setup before creating a TestFeed draft.',
        noMatchingFilterDescription: 'No setups match the selected filter.',
        clearFiltersLabel: 'Clear filters',
    },
    productDetails: {
        noDescription: 'No product description available.',
        noProductName: 'Shopify product',
        noVariantsLabel: 'No variant price available',
        noCollectionsLabel: 'No collections',
        priceRangeLabel: 'Price range',
        handleLabel: 'Handle',
        collectionsLabel: 'Collections',
        photoOfTemplate: 'Photo of ',
    },
    settingsPage: {
        dataSafetyBodyFirst: 'Shopper Intelligence is analyzed when you design a new product test setup. Available store-level patterns can provide context for that setup.',
        dataSafetyBodySecond: 'TestFeed uses aggregated store-level patterns as optional context for simulated shopper profiles. Results are directional. They compare versions inside this test only.',
        privacySafeguardsLabel: 'Privacy Safeguards:',
        privacySafeguardsPii: '• Contact-data boundary: Shopper Intelligence does not request customer names, emails, addresses, or phone numbers.',
        privacySafeguardsStats: '• Aggregated context: Eligible store patterns are summarized before they are used as test context.',
        supportQuestion: 'Need help configuring your TestFeed integration or want to learn more about how simulated shoppers work?',
        supportBody: 'Read our documentation to understand how TestFeed produces directional simulated shopper feedback for early product decisions.',
        documentationLabel: 'Documentation',
        contactSupportLabel: 'Contact Support',
        bookStrategyCallLabel: 'Book a strategy call',
    },
    shopifyErrorBoundary: {
        pageTitle: 'App Error',
        headerTitle: 'Something went wrong',
        message: 'The TestFeed app encountered a critical error. Please reload the page or contact support if the issue persists.',
        errorMessageLabel: 'Error message:',
        stackColor: '#bf0000',
        stackBackground: '#fff3f3',
        stackBorder: '#ffd1d1',
    },
    shopifyHomePage: {
        howItWorks: 'How it works',
        whatYouCanTest: 'What you can test',
    },
    webPixelActivityPage: {
        heading: 'Storefront Web Pixel activity',
        subtext: 'Review the latest Web Pixel survey fetches and response submissions for this store.',
    },
    webPixelActivityLogCard: {
        noActivityTitle: 'No Web Pixel activity yet',
        noActivityBody: 'Open the storefront so the Web Pixel can record standard page, product, cart, checkout, and survey activity.',
        loadingText: 'Loading Web Pixel activity',
        unableToLoadTitle: 'Unable to load Web Pixel activity',
        unableToLoadFallback: 'Web Pixel activity request failed',
        webPixelTitle: 'Web Pixel activity',
        refreshButton: 'Refresh',
        eventLabels: {
            activeSurveyFetch: 'Active survey fetch',
            responseSubmit: 'Response submit',
            triggerReached: 'Trigger reached',
            triggerCandidate: 'Trigger candidate',
            surveyDelivery: 'Survey delivery',
            surveyDismissed: 'Survey dismissed',
            surveyCompleted: 'Survey completed',
            fallback: 'Pixel configuration',
            anyTrigger: 'Any trigger',
            successStatus: 'Success',
            noActiveSurveyStatus: 'No active survey',
            disabledStatus: 'Disabled',
            skippedStatus: 'Skipped',
            needsAttentionStatus: 'Needs attention',
        },
    },
    wizard: {
        stepOf: 'Step',
        of: 'of',
        backButtonLabel: 'Back',
        nextButtonLabel: 'Next',
    },
    studyBuilderPanel: {
        stepTitleSetup: 'Test Setup',
        stepTitleAudience: 'Audience Configuration',
        stepTitleBrief: 'Test Brief',
        stepTitleRun: 'Review & Save Setup',
        saveLabel: 'Save',
        savingLabel: 'Saving…',
        discardLabel: 'Discard',
        discardingLabel: 'Discarding…',
        reconnectLabel: 'Reconnect app',
        progressOfSteps: 4,
    },
    studyBuilderHelpers: {
        discardDraftLabel: 'Discard draft',
    },
    studyBuilderSurveyStep: {
        productContextTitle: 'Product context',
        productContextBody: 'Shopify details used to anchor the product story.',
        productContextPlaceholderBody: 'Select a Shopify product to anchor this test before you continue.',
        changeProductLabel: 'Change product',
        selectProductLabel: 'Select product',
    },
    studySetupsPage: {
        lifecycleAll: 'All',
        lifecycleSetupPrepared: 'Draft',
        lifecycleDraft: 'Draft',
        lifecycleSyncing: 'Running',
        lifecycleRunning: 'Running',
        lifecycleStopped: 'Error',
        lifecycleFailed: 'Error',
        lifecycleReportReady: 'Complete',
    },
    appEmbedActivationCard: {
        heading: 'Storefront UI',
        description: 'The Web Pixel handles the logic, but you must enable the survey UI in your Theme Editor for it to appear to shoppers.',
        ctaLabel: 'Enable UI in Theme Editor',
    },
    shopifyMcpInstallCard: {
        title: 'Shopify MCP',
        intro: 'Add the Shopify MCP endpoint to your MCP client and keep OAuth credentials tied to this store.',
        installStepsTitle: 'Install steps',
        installStepOne: '1) Copy the endpoint below.',
        installStepTwo: '2) Paste it into your MCP client configuration.',
        installScopeFallbackLabel: 'Use this fallback if your MCP client does not support scoped endpoints:',
        scopedEndpointCopyLabel: 'Copy Shopify MCP endpoint',
        copySuccess: 'Copied',
        authFlowInstructions: 'Authenticate with your TestFeed account when your MCP client starts the OAuth flow.',
        authRetryMessage: 'If a session expires or auth fails, log back in to TestFeed and reconnect this Shopify store before retrying.',
        recommendedEndpointPrefix: 'Recommended endpoint for this store:',
        installFallbackTitle: 'Install endpoint',
    },
    shopifySettingsContent: {
        testFeedConnectedTitle: 'TestFeed account connected',
        testFeedConnectedBody: 'This Shopify store is now linked to your active TestFeed organization.',
        connectionSectionTitle: 'TestFeed connection',
        connectionSectionDescription: 'Connect your store to create shopper tests and review results in one place.',
        usageCreditsSectionTitle: 'Available credits',
        usageCreditsSectionDescription: 'Track your monthly credits and activity.',
        mcpSectionTitle: 'MCP setup',
        mcpSectionDescription: 'Install the Shopify MCP endpoint in your MCP client for merchant-facing TestFeed workflows.',
        featuresSectionTitle: 'TestFeed features',
        featuresSectionDescription: 'See what is available for learning from shopper activity in your store.',
        integrationsSectionTitle: 'Integrations',
        integrationsSectionDescription: 'Bring data from the tools you already use into your tests.',
        privacySectionTitle: 'Data and privacy',
        privacySectionDescription: "Learn how TestFeed handles your store's data and protects customer privacy.",
        supportSectionTitle: 'Support and resources',
        supportSectionDescription: 'Find help with your tests or contact support.',
        pixelEventsComingSoonDescription: 'Storefront activity diagnostics and shopper-intent event streams will be available after the private rollout is complete.',
        microSurveysComingSoonDescription: 'Storefront survey prompts will be available after Pixel events are generally available.',
    },
    audienceConfiguration: {
        baseProfileTitle: 'Base Profile',
        baseProfileDescription: 'who these simulated shoppers represent before store background is added.',
    },
    audienceFrameSelector: {
        unavailableTitle: 'Customer-shaped audience unavailable',
        chooseAudienceFrameTitle: 'Choose who answers your test',
    },
    audienceLeverCard: {
        includedForPrefix: 'Included for',
        includedForOfConnector: 'of',
        includedForSuffix: 'simulated shoppers',
        includeAsShopperBackground: 'Include as shopper background',
        shopperBackgroundHelpText: 'Simulated shoppers will react to your concepts with this background.',
        keepAsSetupContext: 'Keep as setup context',
        setupContextHelpText: 'Retain for audience reporting without changing shopper behavior.',
        leaveOut: 'Leave out',
        leaveOutHelpText: 'Do not use this pattern in this test.',
        detailsExpand: 'Details v',
        detailsCollapse: 'Details ^',
        whatThisMeansTitle: 'What this means',
        whyItIsIncludedTitle: 'Why it is included',
        howToReadItTitle: 'How to read it',
        useInThisStudyTitle: 'Use in this test',
        technicalAuditDetailsTitle: 'Technical audit details',
        groundingStatusLabel: 'Grounding status',
        allowedRolesLabel: 'Allowed roles',
        blockedRolesLabel: 'Blocked roles',
        claimBoundaryLabel: 'What this can support',
    },
};
//# sourceMappingURL=shopify-components.js.map